import React, { useEffect, useRef } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import { useGetAgentChatHistoryQuery } from '../../../../api/agentChatApi';
import { CURRENT_TAX_YEAR } from '../../../../constants';

const AgentMessagesSection = () => {
  const selectedUserId = useSelector(selectedUserIdSelector);
  const { data, error, isLoading } = useGetAgentChatHistoryQuery({ userId: selectedUserId, taxYear: CURRENT_TAX_YEAR });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <div>{error.message || 'Failed to load agent messages'}</div>;
  }

  const { note, messages = [] } = data || {};

  return (
    <Box
      data-testid='agent-messages-section'
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {note && (
        <Accordion defaultExpanded sx={{ flexShrink: 0 }} variant='outlined'>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>Agent Note</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontWeight: 'bold' }}>{note}</Typography>
          </AccordionDetails>
        </Accordion>
      )}
      <Box sx={{ overflow: 'auto', flex: 1, p: 1 }}>
        {messages.length === 0 && !note && (
          <Typography
            sx={{
              color: 'text.secondary'
            }}>
            No agent messages found.
          </Typography>
        )}
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.isUserMessage ? 'flex-end' : 'flex-start',
              mb: 1
            }}>
            <Paper
              sx={{
                p: 1.5,
                maxWidth: '80%',
                backgroundColor: message.isUserMessage ? 'primary.light' : 'grey.100',
                color: message.isUserMessage ? 'primary.contrastText' : 'text.primary'
              }}>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {message.content}
              </Typography>
            </Paper>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                mt: 0.25,
                px: 0.5
              }}>
              {message.isUserMessage ? 'User' : 'Agent'} &middot; {new Date(message.createdAt).toLocaleString()}
            </Typography>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default AgentMessagesSection;
